/* k-polish.js – 2026-10-09 (theme "Kukur · live 0409 · be pokalbio"). Roll back by removing the two k-polish tags from layout/theme.liquid.
   1 predictive search: keep the panel open while a result is clicked/tapped (focusout fired before the click landed)
   2 quick-link pills: fit on one row at >=1024 px, overflow goes into a "Daugiau" dropdown
   3 product gallery by colour (variant.featured_media + alt/file name), arrows + swipe
   4 lightbox gallery (arrows, keys, swipe, thumbnails, counter, focus trap)
   5 gift filters: counts, hide empty values, recipients from gavejas:* tags */
(() => {
  'use strict';
  const fold = s => (s || '').toLocaleLowerCase('lt').normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  /* 1 ─ predictive search */
  let searchPointer = null;
  document.addEventListener('pointerdown', e => { searchPointer = e.target.closest?.('k-live-search .k-search-panel') || null; }, true);
  document.addEventListener('pointerup', () => setTimeout(() => { searchPointer = null; }, 400), true);
  document.addEventListener('focusout', e => {
    const box = e.target.closest?.('k-live-search');
    if (!box) return;
    if ((e.relatedTarget && box.contains(e.relatedTarget)) || (searchPointer && box.contains(searchPointer))) e.stopImmediatePropagation();
  }, true);

  /* 2 ─ pill row fit */
  const fitChips = nav => {
    const more = nav.querySelector(':scope > .k15-chip-more');
    if (!more) return;
    const list = more.querySelector('.k15-chip-more__list');
    const pills = [...nav.querySelectorAll(':scope > a.k-icon-link')];
    pills.forEach(p => p.classList.remove('k-fit-hidden'));
    list.replaceChildren(); more.hidden = true; more.open = false;
    if (innerWidth < 1024) { nav.classList.remove('is-fit'); return; }
    nav.classList.add('is-fit');
    const limit = () => nav.getBoundingClientRect().right - parseFloat(getComputedStyle(nav).paddingRight || 0) + 0.5;
    const overflowing = () => { const vis = [...nav.children].filter(el => !el.hidden && !el.classList.contains('k-fit-hidden')); const last = vis.at(-1); return last && last.getBoundingClientRect().right > limit(); };
    if (!overflowing()) return;
    more.hidden = false;
    for (let i = pills.length - 1; i > 0 && overflowing(); i--) {
      pills[i].classList.add('k-fit-hidden');
      const copy = pills[i].cloneNode(true); copy.classList.remove('k-fit-hidden'); list.prepend(copy);
    }
  };
  const fitAll = () => document.querySelectorAll('.k15-type-chips[data-k-fit]').forEach(fitChips);
  let fitTimer; addEventListener('resize', () => { clearTimeout(fitTimer); fitTimer = setTimeout(fitAll, 120); });
  document.addEventListener('click', e => { document.querySelectorAll('.k15-chip-more[open]').forEach(d => { if (!d.contains(e.target)) d.open = false; }); });
  document.addEventListener('keydown', e => { if (e.key !== 'Escape') return; const d = document.activeElement?.closest?.('.k15-chip-more[open]'); if (d) { d.open = false; d.querySelector('summary').focus(); } });

  /* 3 ─ product gallery by colour */
  const COLOR_WORDS = { balta: ['balta', 'white', 'balt'], juoda: ['juoda', 'black', 'juod'], 'tamsiai melyna': ['tamsiai-melyna', 'tamsiai melyna', 'navy', 'melyna'], naturali: ['naturali', 'natural', 'smelin', 'beige', 'sand'], smeline: ['smelin', 'beige', 'sand', 'naturali', 'natural'], bordo: ['bordo', 'burgundy', 'maroon'], 'misko zalia': ['misko-zalia', 'misko zalia', 'forest', 'zalia', 'green'], raudona: ['raudona', 'red'], pilka: ['pilka', 'grey', 'gray'] };
  const LATE = /(back|nugar|galas|gale|lifestyle|gyvenim|model|zmog|size|dydz|lentel|chart)/;
  const CHART = /(size|dydz|lentel|chart)/;
  const tokensFor = color => { const k = fold(color).replace(/\s+/g, ' ').trim(); return COLOR_WORDS[k] || [k, k.replace(/ /g, '-')]; };
  const textOf = m => fold(m.alt + ' ' + m.file.replace(/[_.]/g, '-'));
  const wordHit = (text, token) => new RegExp('(^|[^a-z])' + token.replace(/[-\s]/g, '[-\\s_]') + '([^a-z]|$)').test(text);

  class Gallery {
    constructor(section) {
      this.section = section;
      this.media = JSON.parse(section.querySelector('[data-k-media]')?.textContent || '[]');
      this.product = section.querySelector('k-product');
      this.variants = JSON.parse(this.product?.querySelector('[data-variants]')?.textContent || '[]');
      this.colorIndex = Number(this.product?.dataset.colorIndex ?? -1);
      this.hero = section.querySelector('.main-photo [data-zoom]');
      this.thumbsBox = section.querySelector('.gallery-thumbs');
      this.thumbs = new Map([...section.querySelectorAll('.gallery-thumbs .thumb')].map(t => [String(t.dataset.imageId), t]));
      this.byId = new Map(this.media.map(m => [String(m.id), m]));
      this.buildSets();
      this.addControls();
      const color = this.currentColor();
      this.apply(color, true);
      this.product?.addEventListener('click', e => { if (e.target.closest('.k-color-choice')) setTimeout(() => this.apply(this.currentColor()), 0); });
      this.product?.addEventListener('change', e => { if (e.target.matches('select[data-option-index]')) setTimeout(() => this.apply(this.currentColor()), 0); });
    }
    colors() { if (this.colorIndex < 0) return []; return [...new Set(this.variants.map(v => v.options[this.colorIndex]))]; }
    buildSets() {
      this.sets = new Map();
      const colors = this.colors();
      const tied = new Map(this.media.map(m => [String(m.id), new Set()]));
      for (const c of colors) {
        for (const v of this.variants) if (v.options[this.colorIndex] === c && v.featured_image) tied.get(String(v.featured_image.id))?.add(c);
        const toks = tokensFor(c);
        for (const m of this.media) if (toks.some(t => wordHit(textOf(m), fold(t)))) tied.get(String(m.id)).add(c);
      }
      const rank = m => (CHART.test(textOf(m)) ? 3 : LATE.test(textOf(m)) ? 2 : 0);
      const shared = this.media.filter(m => !tied.get(String(m.id)).size).sort((a, b) => (CHART.test(textOf(a)) ? 1 : 0) - (CHART.test(textOf(b)) ? 1 : 0));
      this.all = [...this.media].sort((a, b) => rank(a) - rank(b));
      for (const c of colors) {
        const front = [];
        for (const v of this.variants) if (v.options[this.colorIndex] === c && v.featured_image) { const m = this.byId.get(String(v.featured_image.id)); if (m && !front.includes(m) && rank(m) === 0) front.push(m); }
        const own = this.media.filter(m => tied.get(String(m.id)).has(c) && !front.includes(m)).sort((a, b) => rank(a) - rank(b));
        const set = [...front, ...own];
        this.sets.set(c, set.length ? [...set, ...shared] : this.all);
      }
    }
    currentColor() {
      if (this.colorIndex < 0) return null;
      const pressed = this.section.querySelector('.k-color-choice[aria-pressed="true"]');
      if (pressed) return pressed.dataset.colorValue;
      const sel = this.section.querySelector(`select[data-option-index="${this.colorIndex}"]`);
      if (sel?.value) return sel.value;
      const id = new URL(location.href).searchParams.get('variant');
      const v = this.variants.find(x => String(x.id) === id) || this.variants.find(x => x.available) || this.variants[0];
      return v ? v.options[this.colorIndex] : null;
    }
    get list() { return (this.color && this.sets.get(this.color)) || this.all; }
    apply(color, initial) {
      const changed = color !== this.color;
      this.color = color;
      if (this.thumbsBox && this.thumbs.size) {
        const ids = this.list.map(m => String(m.id));
        for (const [id, t] of this.thumbs) t.classList.toggle('k-thumb-off', !ids.includes(id));
        ids.forEach(id => { const t = this.thumbs.get(id); if (t) this.thumbsBox.append(t); });
      }
      if (changed || initial) this.show(0);
      this.updateArrows();
    }
    index() { const cur = this.hero?.dataset.src || ''; const i = this.list.findIndex(m => m.zoom === cur); return i < 0 ? 0 : i; }
    show(i) {
      const list = this.list; if (!list.length || !this.hero) return;
      i = (i + list.length) % list.length;
      const m = list[i], img = this.hero.querySelector('img');
      if (img && img.getAttribute('src') !== m.src) {
        img.src = m.src;
        img.srcset = [400,800,1200].filter(w=>w<=m.w).map(w=>{const url=new URL(m.src,location.href);url.searchParams.set('width',w);return url.href+' '+w+'w';}).join(', ');
      }
      if (img) img.alt = m.alt || img.alt;
      this.hero.dataset.src = m.zoom; this.hero.dataset.alt = m.alt || this.hero.dataset.alt || '';
      this.thumbs.forEach((t, id) => t.setAttribute('aria-pressed', String(id === String(m.id))));
      if (this.counter) this.counter.textContent = `${i + 1} / ${list.length}`;
    }
    step(d) { this.show(this.index() + d); }
    addControls() {
      const box = this.section.querySelector('.main-photo');
      if (!box || !this.hero || this.media.length < 2) return;
      box.classList.add('k-has-arrows');
      const mk = (cls, label, d, path) => { const b = document.createElement('button'); b.type = 'button'; b.className = 'k-gal-arrow ' + cls; b.setAttribute('aria-label', label); b.innerHTML = `<svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="${path}" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`; b.addEventListener('click', e => { e.preventDefault(); e.stopPropagation(); this.step(d); }); box.append(b); return b; };
      this.prev = mk('k-gal-prev', 'Ankstesnė nuotrauka', -1, 'M12.5 4l-6 6 6 6');
      this.next = mk('k-gal-next', 'Kita nuotrauka', 1, 'M7.5 4l6 6-6 6');
      this.counter = document.createElement('span'); this.counter.className = 'k-gal-count'; this.counter.setAttribute('aria-hidden', 'true'); box.append(this.counter);
      let sx = null, sy = null; this.swiped = false;
      box.addEventListener('touchstart', e => { sx = e.touches[0].clientX; sy = e.touches[0].clientY; this.swiped = false; }, { passive: true });
      box.addEventListener('touchend', e => { if (sx === null) return; const dx = e.changedTouches[0].clientX - sx, dy = e.changedTouches[0].clientY - sy; if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) { this.swiped = true; this.step(dx < 0 ? 1 : -1); } sx = null; }, { passive: true });
      box.addEventListener('click', e => { if (this.swiped) { e.preventDefault(); e.stopPropagation(); this.swiped = false; } }, true);
      this.section.querySelector('.gallery-thumbs')?.addEventListener('click', e => { if (e.target.closest('.thumb')) setTimeout(() => this.show(this.index()), 0); });
    }
    updateArrows() { const n = this.list.length; [this.prev, this.next].forEach(b => b && (b.hidden = n < 2)); if (this.counter) { this.counter.hidden = n < 2; this.counter.textContent = `${this.index() + 1} / ${n}`; } }
  }

  /* 4 ─ lightbox */
  let lb = null;
  const lightbox = () => {
    if (lb) return lb;
    const d = document.createElement('dialog');
    d.className = 'k-lb'; d.setAttribute('aria-label', 'Nuotraukų galerija');
    d.innerHTML = '<div class="k-lb__top"><span class="k-lb__count" aria-live="polite"></span><button type="button" class="k-lb__close" aria-label="Uždaryti"><svg width="22" height="22" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg></button></div>' +
      '<div class="k-lb__stage"><button type="button" class="k-lb__nav k-lb__prev" aria-label="Ankstesnė nuotrauka"><svg width="24" height="24" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M12.5 4l-6 6 6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button><img class="k-lb__img" alt=""><button type="button" class="k-lb__nav k-lb__next" aria-label="Kita nuotrauka"><svg width="24" height="24" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M7.5 4l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button></div>' +
      '<div class="k-lb__thumbs" role="group" aria-label="Nuotraukos"></div>';
    document.body.append(d);
    lb = { d, img: d.querySelector('.k-lb__img'), count: d.querySelector('.k-lb__count'), thumbs: d.querySelector('.k-lb__thumbs'), list: [], i: 0, opener: null, gallery: null };
    const go = i => { const n = lb.list.length; if (!n) return; lb.i = (i + n) % n; const m = lb.list[lb.i]; lb.img.src = m.zoom; lb.img.alt = m.alt || ''; lb.count.textContent = `${lb.i + 1} / ${n}`; [...lb.thumbs.children].forEach((t, k) => { t.setAttribute('aria-current', String(k === lb.i)); if (k === lb.i) t.scrollIntoView({ block: 'nearest', inline: 'nearest' }); }); d.querySelectorAll('.k-lb__nav').forEach(b => b.hidden = n < 2); };
    lb.go = go;
    d.querySelector('.k-lb__prev').addEventListener('click', () => go(lb.i - 1));
    d.querySelector('.k-lb__next').addEventListener('click', () => go(lb.i + 1));
    d.querySelector('.k-lb__close').addEventListener('click', () => d.close());
    d.addEventListener('click', e => { if (e.target === d) d.close(); });
    d.addEventListener('keydown', e => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(lb.i - 1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); go(lb.i + 1); }
      else if (e.key === 'Tab') { const f = [...d.querySelectorAll('button:not([hidden])')].filter(b => b.offsetParent !== null); if (!f.length) return; const first = f[0], last = f.at(-1); if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); } else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); } }
    });
    let sx = null;
    lb.img.addEventListener('touchstart', e => { sx = e.touches[0].clientX; }, { passive: true });
    lb.img.addEventListener('touchend', e => { if (sx === null) return; const dx = e.changedTouches[0].clientX - sx; if (Math.abs(dx) > 40) go(lb.i + (dx < 0 ? 1 : -1)); sx = null; }, { passive: true });
    d.addEventListener('close', () => { document.body.classList.remove('modal-open'); if (lb.gallery) lb.gallery.show(lb.i); lb.opener?.focus({ preventScroll: true }); });
    return lb;
  };
  const openLightbox = (gallery, opener) => {
    const l = lightbox();
    l.gallery = gallery; l.opener = opener; l.list = gallery.list;
    l.thumbs.replaceChildren(...l.list.map((m, k) => { const b = document.createElement('button'); b.type = 'button'; b.className = 'k-lb__thumb'; b.setAttribute('aria-label', `Nuotrauka ${k + 1} iš ${l.list.length}`); b.innerHTML = `<img src="${m.thumb}" alt="" loading="lazy" width="64" height="64">`; b.addEventListener('click', () => l.go(k)); return b; }));
    l.thumbs.hidden = l.list.length < 2;
    l.d.showModal(); document.body.classList.add('modal-open');
    l.go(gallery.index());
    l.d.querySelector('.k-lb__close').focus();
  };
  const galleries = new WeakMap();
  addEventListener('click', e => {
    const zoom = e.target.closest?.('.product-gallery [data-zoom]');
    if (!zoom) return;
    const section = zoom.closest('[data-product-section]');
    const g = section && galleries.get(section);
    if (!g || !g.media.length) return;
    e.preventDefault(); e.stopPropagation();
    openLightbox(g, zoom);
  }, true);

  /* 5 ─ gift filters */
  const RECIPIENTS = { vyrams: 'Vyrams', moterims: 'Moterims', mamai: 'Mamai', teciui: 'Tėčiui', draugams: 'Draugams', kolegai: 'Kolegai', bosui: 'Bosui', bosei: 'Bosei', mociutei: 'Močiutei', seneliui: 'Seneliui', vaikams: 'Vaikams', kudikiams: 'Kūdikiams', tetis: 'Tėčiui', father: 'Tėčiui', draugas: 'Draugui', friend: 'Draugui', grandfather: 'Seneliui', seneliai: 'Seneliams', sau: 'Sau', self: 'Sau', kolega: 'Kolegai', vaikas: 'Vaikui' };
  /* occasions: proga-x / proga:x / occasion:x are one value -> canonical "occasion:x" */
  const OCC_RE = /^(proga[-:]|occasion:)/;
  const OCCASIONS = { gimtadienis: 'Gimtadienis', kaledos: 'Kalėdos', 'vyru-diena': 'Vyrų diena', 'bosu-diena': 'Bosų diena', 'tevo-diena': 'Tėvo diena', 'motinos-diena': 'Motinos diena', 'valentino-diena': 'Valentino diena', 'naujieji-metai': 'Naujieji metai', velykos: 'Velykos', vardadienis: 'Vardadienis', sukaktis: 'Sukaktis', vestuves: 'Vestuvės', krikstynos: 'Krikštynos', 'draugystes-diena': 'Draugystės diena', 'krepsinio-vakaras': 'Krepšinio vakaras', 'seimos-diena': 'Šeimos diena', 'sv-hubertas': 'Šv. Hubertas', 'kariuomenes-diena': 'Kariuomenės diena', krepsinis: 'Krepšinis', zvejyba: 'Žvejyba' };
  const occKey = v => fold(String(v || '').replace(OCC_RE, '')).trim().replace(/[\s_]+/g, '-');
  const occLabel = k => OCCASIONS[k] || (k.charAt(0).toLocaleUpperCase('lt') + k.slice(1).replaceAll('-', ' '));
  const normalizeOccasions = el => {
    if (el.__kOcc || !el.items) return;
    for (const it of el.items) {
      const keys = new Set(it.tags.filter(t => OCC_RE.test(t)).map(occKey).filter(Boolean));
      if (!keys.size) continue;
      it.tags = it.tags.filter(t => !OCC_RE.test(t)).concat([...keys].map(k => 'occasion:' + k));
    }
    el.__kOcc = true;
  };
  const canonOccField = el => {
    const f = el.fields?.find(x => x.name === 'k17_occasion');
    if (!f) return;
    const want = f.value || new URL(location.href).searchParams.get(f.name);
    if (want && OCC_RE.test(want)) { const c = 'occasion:' + occKey(want); if ([...f.options].some(o => o.value === c)) f.value = c; }
    for (const o of f.options) if (o.value) { const k = occKey(o.value); o.textContent = occLabel(k); delete o.dataset.kLabel; }
  };
  const NICE = { darzas: 'Daržas', seima: 'Šeima', zvejyba: 'Žvejyba', medziokle: 'Medžioklė', grybai: 'Grybai', pirtis: 'Pirtis', kava: 'Kava', sportas: 'Sportas', krepsinis: 'Krepšinis', zodiakas: 'Zodiakas', gyvunai: 'Gyvūnai', kelione: 'Kelionės', keliones: 'Kelionės', darbas: 'Darbas', it: 'IT', mokytojai: 'Mokytojai', medikai: 'Medikai', pomegiai: 'Pomėgiai' };
  const niceExtra = el => {
    for (const f of el.fields?.slice(2) || []) {
      if (f.name === 'k17_occasion') continue;
      for (const o of f.options) if (o.value && !o.dataset.kNice) { const k = fold(o.value.replace(/^[^:-]+[:-]/, '')).replace(/\s+/g, '-'); o.textContent = NICE[k] || (k.charAt(0).toLocaleUpperCase('lt') + k.slice(1).replaceAll('-', ' ')); o.dataset.kNice = '1'; delete o.dataset.kLabel; }
    }
  };
  const typeMatch = (item,type) => customElements.get('k15-gift-filters').matchesType(item,type);
  const recMatch = (it, r) => it.tags.includes(r) || it.tags.includes('gavejas:' + r) || it.tags.includes('gavėjas:' + r);
  const label = (o, n) => { o.dataset.kLabel ||= o.textContent.replace(/\s*\(\d+\)$/, ''); o.textContent = `${o.dataset.kLabel} (${n})`; };
  const enhanceFilters = el => {
    const items = el.items; if (!items || !el.fields) return;
    const [typeSel, recSel, ...extra] = el.fields;
    const prune = (sel, count) => { if (!sel) return; for (const o of [...sel.options]) { if (!o.value) continue; const n = count(o.value); if (!n && o.value !== sel.value) o.remove(); else label(o, n); } };
    if (recSel && !recSel.dataset.kBuilt) {
      const found = new Set(Object.keys(RECIPIENTS).filter(r => items.some(it => recMatch(it, r))));
      items.forEach(it => it.tags.forEach(t => { const m = /^gav[eė]jas:(.+)$/.exec(t); if (m) found.add(m[1]); }));
      const have = new Set([...recSel.options].map(o => o.value));
      for (const r of found) if (!have.has(r)) { const o = document.createElement('option'); o.value = r; o.textContent = RECIPIENTS[r] || r.charAt(0).toLocaleUpperCase('lt') + r.slice(1).replaceAll('-', ' '); recSel.append(o); }
      const want = new URL(location.href).searchParams.get(recSel.name); if (want) recSel.value = want;
      recSel.dataset.kBuilt = '1';
    }
    prune(typeSel, v => items.filter(it => typeMatch(it, v)).length);
    prune(recSel, v => items.filter(it => recMatch(it, v)).length);
    extra.forEach(sel => prune(sel, v => items.filter(it => it.tags.includes(v)).length));
    for (const sel of [typeSel, recSel, ...extra]) if (sel) { sel.disabled = sel.options.length <= 1; sel.closest('label').hidden = sel.disabled; }
  };
  customElements.whenDefined('k15-gift-filters').then(() => {
    const C = customElements.get('k15-gift-filters');
    if (C && !C.prototype.__kPolish) {
      const orig = C.prototype.populateExtraFilters;
      C.prototype.populateExtraFilters = function () { try { normalizeOccasions(this); } catch (e) { console.warn('k-polish occasions', e); } orig?.call(this); try { canonOccField(this); niceExtra(this); enhanceFilters(this); } catch (e) { console.warn('k-polish filters', e); } };
      C.prototype.__kPolish = true;
    }
    document.querySelectorAll('k15-gift-filters').forEach(el => { if (el.items) el.populateExtraFilters(); });
  });


  /* 6 ─ compact chrome: mobile search toggle, collection filter toggle, closed description, sticky buy bar only after the in-form ATC */
  const compactChrome = () => {
    const actions = document.querySelector('.header-inner .header-actions');
    if (actions && !actions.querySelector('.k-header-search-toggle') && !document.body.classList.contains('page-search')) {
      const b = document.createElement('button');
      b.type = 'button'; b.className = 'k-header-search-toggle'; b.setAttribute('aria-expanded', 'false'); b.setAttribute('aria-controls', 'HeaderSearch');
      b.innerHTML = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false"><circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" stroke-width="2"/><path d="M16 16l4.5 4.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg><span>Paieška</span>';
      b.addEventListener('click', () => { const open = document.body.classList.toggle('k-search-open'); b.setAttribute('aria-expanded', String(open)); if (open) document.getElementById('HeaderSearch')?.focus(); });
      actions.prepend(b);
    }
    document.querySelectorAll('details.collection-description').forEach(d => { d.open = true; });

  };

  /* 7 ─ Shopify privacy banner: lift sticky ATC + other bottom-fixed elements while the banner is visible (banner itself untouched) */
  const PC_SEL = '#shopify-pc__banner';
  const pcSync = () => {
    const b = document.querySelector(PC_SEL);
    const r = b && b.checkVisibility?.() ? b.getBoundingClientRect() : null;
    const h = r && r.height > 0 && r.bottom >= innerHeight - 2 ? Math.ceil(innerHeight - r.top) : 0;
    const root = document.documentElement;
    root.style.setProperty('--k-pc-h', h + 'px');
    document.body.classList.toggle('k-pc-open', h > 0);
    document.querySelectorAll('body *').forEach(el => {
      if (el === b || b?.contains(el) || el.closest('dialog,.k-lb')) return;
      if (el.classList.contains('k-pc-lift')) { if (!h) el.classList.remove('k-pc-lift'); return; }
      if (!h) return;
      const cs = getComputedStyle(el);
      if (cs.position !== 'fixed' || cs.display === 'none') return;
      const er = el.getBoundingClientRect();
      if (er.height > 0 && er.height < innerHeight * 0.5 && er.bottom > innerHeight - 40) el.classList.add('k-pc-lift');
    });
  };
  let pcTimer;
  const pcQueue = () => { clearTimeout(pcTimer); pcTimer = setTimeout(pcSync, 80); };
  const pcWatch = () => {
    pcSync();
    new MutationObserver(muts => { if (muts.some(m => m.target.nodeType === 1 && (m.target.matches?.(PC_SEL) || m.target.closest?.(PC_SEL) || [...m.addedNodes, ...m.removedNodes].some(n => n.nodeType === 1 && (n.matches?.(PC_SEL) || n.querySelector?.(PC_SEL)))))) pcQueue(); })
      .observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'hidden', 'aria-hidden'] });
    addEventListener('resize', pcQueue);
    document.addEventListener('visitorConsentCollected', pcQueue);
    setTimeout(pcSync, 1500); setTimeout(pcSync, 4000);
  };
  const initGalleries = (scope=document) => scope.querySelectorAll('[data-product-section]').forEach(s => { if (!galleries.has(s) && s.querySelector('[data-k-media]')) galleries.set(s, new Gallery(s)); });
  document.addEventListener('shopify:section:load', event => { compactChrome(); fitAll(); initGalleries(event.target); });
  /* init */
  const init = () => {
    try { compactChrome(); } catch (e) { console.warn('k-polish chrome', e); }
    try { pcWatch(); } catch (e) { console.warn('k-polish pc', e); }
    fitAll();
    initGalleries();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  addEventListener('load', fitAll);
  document.fonts?.ready.then(fitAll);
})();
